import { useState, useEffect, useContext } from 'react';
import {
    Avatar,
    Dialog,
    DialogTitle,
    List,
    ListItemText,
    ListItemButton,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { AppContext, SttList } from '../all';

type Sport = {
    id: number;
};

type SCAdmin = {
    id: number;
    name: string;
    login: string;
    sport?: Sport;
};

type Props = {
    scAdmins: SCAdmin[];
    onCreate?: () => void;
    onAdd?: (scAdmin: SCAdmin) => void;
    onDelete?: (scAdmin: SCAdmin) => void;
    onSelect?: (scAdmin: SCAdmin) => void;
};

/**
 *    SCAdmin list
 *    ============
 *
 *    Props
 *
 *        * array scAdmins
 *        * function onAdd: after adding from the list
 *        * function onCreate: instead of picking from the list just call this func
 *        * function onDelete
 *        * functino onSelect
 */
export function SttSCAdminList({
    scAdmins,
    onCreate,
    onAdd,
    onDelete,
    onSelect,
}: Props): React.ReactElement {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const [selectingSCAdminOpen, setSelectingSCAdminOpen] = useState(false);
    const [allSCAdmins, setAllSCAdmins] = useState<SCAdmin[]>();

    /**
     * Load all scAdmins
     */
    useEffect(() => {
        // Fetch all scAdmins on the sports center
        if (selectingSCAdminOpen && !allSCAdmins) {
            cxt?.api('GET', `/scs/${cxt?.sc?.id}/admins`, {
                success: (r: { rows?: SCAdmin[] }) => {
                    if (r.rows) {
                        setAllSCAdmins(r.rows);
                    }
                },
            });
        }
    }, [selectingSCAdminOpen, allSCAdmins, cxt]);

    /**
     * Pre-adding
     */
    const preAdd = () => {
        if (onCreate) {
            onCreate();
        } else if (onAdd) {
            setSelectingSCAdminOpen(true);
        }
    };

    /**
     * Select scAdmin
     */
    const selectSCAdmin = (scAdmin: SCAdmin) => {
        setSelectingSCAdminOpen(false);
        onAdd?.(scAdmin);
    };

    // -----| Render |-----
    return (
        <>
            <SttList
                title={t('configuration.administrators')}
                onAdd={() => preAdd()}
                onDelete={onDelete}
                onSelect={onSelect}
                data={
                    scAdmins &&
                    scAdmins.map((scAdmin: any) => ({
                        avatar: scAdmin.sport ? (
                            <Avatar
                                style={{
                                    width: 30,
                                    height: 30,
                                    borderWidth: 1,
                                    borderStyle: 'solid',
                                    borderColor: '#ddd',
                                    padding: 2,
                                }}
                                src={cxt?.getResource(
                                    `/sports/${scAdmin.sport.id}.png`,
                                )}
                            />
                        ) : undefined,
                        caption: scAdmin.name,
                        text: `@${scAdmin.login}`,
                        objToSelect: scAdmin,
                    }))
                }
            />

            <Dialog
                onClose={() => setSelectingSCAdminOpen(false)}
                open={selectingSCAdminOpen}
            >
                <DialogTitle>{t('configuration.administrators')}</DialogTitle>

                <List>
                    {allSCAdmins?.map((scAdmin: any) => (
                        <ListItemButton
                            key={scAdmin?.id}
                            onClick={() => selectSCAdmin(scAdmin)}
                        >
                            <ListItemText
                                primary={scAdmin.name}
                                secondary={`@${scAdmin.login}`}
                            />
                        </ListItemButton>
                    ))}
                </List>
            </Dialog>
        </>
    );
}
